import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Toolbar,
  Box,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  IconButton,
  Avatar,
  Menu,
  Typography,
  Divider,
  Button,
  Badge,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import {
  Menu as MenuIcon,
  ShoppingCart,
  AccountCircle,
  Store,
  ExitToApp,
  Search,
  Person,
} from "@mui/icons-material";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import ThemeModeToggle from "../../components/ThemeModeToggle";
import BrandLogo from "../../components/BrandLogo";
import { HeaderCategory } from "./main-layout.types";

interface HeaderTopBarProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
  isRTL: boolean;
  user: any;
  search: string;
  category: string;
  condition: string;
  priceRange: string;
  categories: HeaderCategory[];
  anchorElUser: HTMLElement | null;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterChange: (
    key: "category" | "condition" | "priceRange",
    value: string,
  ) => void;
  onCategoryNavigate: (value: string) => void;
  onOpenUserMenu: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseUserMenu: () => void;
  onSignOut: () => void;
  onDrawerToggle: () => void;
  getItemCount: () => number;
}

const HeaderTopBar: React.FC<HeaderTopBarProps> = ({
  t,
  theme,
  isRTL,
  user,
  search,
  category,
  condition,
  priceRange,
  categories,
  anchorElUser,
  onSearchChange,
  onFilterChange,
  onCategoryNavigate,
  onOpenUserMenu,
  onCloseUserMenu,
  onSignOut,
  onDrawerToggle,
  getItemCount,
}) => {
  return (
    <Toolbar
      disableGutters
      sx={{
        py: 1,
        px: 2,
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
        flexDirection: "row",
        direction: "ltr",
        minHeight: { xs: 68, sm: 78 },
        overflow: "visible",
      }}
    >
      <Box
        component={RouterLink}
        to="/"
        sx={{
          flexGrow: { xs: 1, sm: 0 },
          marginInlineEnd: { xs: "auto", sm: 2 },
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "flex-start",
          alignSelf: "stretch",
          textDecoration: "none",
          transition: "transform 0.3s ease, filter 0.3s ease",
          minWidth: 250,
          flexShrink: 0,
          overflow: "visible",
          "&:hover": {
            transform: "translateY(-1px)",
            filter: "drop-shadow(0 0 16px rgba(0, 71, 255, 0.18))",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            width: 250,
            minHeight: { xs: 44, sm: 48 },
            overflow: "visible",
          }}
        >
          <BrandLogo />
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 4,
          minWidth: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("common.searchPlaceholder")}
              value={search}
              onChange={onSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position={isRTL ? "end" : "start"}>
                    <Search sx={{ color: "#00d4ff" }} />
                  </InputAdornment>
                ),
                sx: {
                  "& input": {
                    color: theme.palette.text.primary,
                    py: 1,
                    textAlign: isRTL ? "right" : "left",
                  },
                },
              }}
              sx={{
                flex: 4,
                maxWidth: 1400,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 10,
                },
              }}
            />
            <FormControl
              size="small"
              margin="dense"
              sx={{ minWidth: { xs: 150, md: 140, lg: 180 } }}
            >
              <InputLabel sx={{ color: theme.palette.text.secondary }}>
                {t("products.categories")}
              </InputLabel>
              <Select
                value={category}
                label={t("products.categories")}
                onChange={(e) => {
                  const value = e.target.value as string;
                  onFilterChange("category", value);
                  onCategoryNavigate(value);
                }}
                sx={{
                  borderRadius: 10,
                  marginRight: isRTL ? 1 : 0,
                  color: theme.palette.text.primary,
                }}
              >
                <MenuItem value="">
                  <em>{t("products.allCategories")}</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.key} value={cat.key}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              margin="dense"
              sx={{ minWidth: { xs: 120, md: 120, lg: 180 } }}
            >
              <InputLabel sx={{ color: theme.palette.text.secondary }}>
                {t("products.condition", { defaultValue: "Condition" })}
              </InputLabel>
              <Select
                value={condition}
                label={t("products.condition", {
                  defaultValue: "Condition",
                })}
                onChange={(e) =>
                  onFilterChange("condition", e.target.value as string)
                }
                sx={{
                  borderRadius: 10,
                  color: theme.palette.text.primary,
                  "& .MuiSelect-select": {
                    textAlign: isRTL ? "right" : "left",
                  },
                }}
              >
                <MenuItem value="">
                  <em>
                    {t("products.allConditions", {
                      defaultValue: "All Conditions",
                    })}
                  </em>
                </MenuItem>
                <MenuItem value="excellent">{t("products.excellent")}</MenuItem>
                <MenuItem value="good">{t("products.good")}</MenuItem>
                <MenuItem value="fair">{t("products.fair")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              margin="dense"
              sx={{ minWidth: { xs: 150, md: 140, lg: 180 } }}
            >
              <InputLabel sx={{ color: theme.palette.text.secondary }}>
                {t("products.priceRange")}
              </InputLabel>
              <Select
                value={priceRange}
                label={t("products.priceRange")}
                onChange={(e) =>
                  onFilterChange("priceRange", e.target.value as string)
                }
                sx={{
                  borderRadius: 10,
                  color: theme.palette.text.primary,
                  "& .MuiSelect-select": {
                    textAlign: isRTL ? "right" : "left",
                  },
                }}
              >
                <MenuItem value="">
                  <em>
                    {t("products.allPrices", {
                      defaultValue: "All Prices",
                    })}
                  </em>
                </MenuItem>
                <MenuItem value="0-5000">{t("products.under5000")}</MenuItem>
                <MenuItem value="5000-20000">
                  {t("products.5000to20000")}
                </MenuItem>
                <MenuItem value="20000-50000">
                  {t("products.20000to50000")}
                </MenuItem>
                <MenuItem value="50000-100000">
                  {t("products.50000to100000")}
                </MenuItem>
                <MenuItem value="100000-">{t("products.over100000")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: { xs: 0, md: user ? 0 : 1 },
          display: "flex",
          gap: { xs: 0.5, sm: 2 },
          alignItems: "center",
          justifyContent: {
            xs: "flex-end",
            md: user ? "flex-end" : "center",
          },
          textAlign: "center",
        }}
      >
        {user ? (
          <>
            <Tooltip title={t("nav.profile")} arrow>
              <IconButton
                onClick={onOpenUserMenu}
                sx={{
                  p: 0,
                  display: { xs: "none", md: "flex" },
                  border: "2px solid rgba(0,212,255,0.3)",
                  "&:hover": {
                    borderColor: "#00d4ff",
                    boxShadow: "0 0 20px rgba(0,212,255,0.5)",
                  },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "rgba(0,212,255,0.2)",
                    border: "1px solid rgba(0,212,255,0.3)",
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                  }}
                >
                  <AccountCircle
                    sx={{
                      color: "#00d4ff",
                      fontSize: { xs: 20, sm: 24 },
                    }}
                  />
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={onCloseUserMenu}
              PaperProps={{
                sx: {
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0,212,255,0.2)"
                      : "0 4px 12px rgba(0,0,0,0.1)",
                  mt: 1,
                  "& .MuiMenuItem-root": {
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: "rgba(0,212,255,0.1)",
                      pl: 3,
                    },
                  },
                },
              }}
            >
              <MenuItem
                component={RouterLink}
                to="/profile"
                onClick={onCloseUserMenu}
              >
                <Person sx={{ mr: 2, color: "#00d4ff" }} />
                <Typography>{t("nav.profile")}</Typography>
              </MenuItem>
              <MenuItem
                component={RouterLink}
                to="/orders"
                onClick={onCloseUserMenu}
              >
                <Store sx={{ mr: 2, color: "#00d4ff" }} />
                <Typography>{t("nav.orders")}</Typography>
              </MenuItem>
              <Divider
                sx={{
                  my: 1,
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                }}
              />
              <MenuItem onClick={onSignOut}>
                <ExitToApp sx={{ mr: 2, color: "#ff0080" }} />
                <Typography color="#ff0080">{t("nav.logout")}</Typography>
              </MenuItem>
            </Menu>
            <Tooltip title={t("nav.cart")} arrow>
              <IconButton
                component={RouterLink}
                to="/cart"
                sx={{
                  position: "relative",
                  display: { xs: "none", md: "flex" },
                  color: theme.palette.text.primary,
                  "&:hover": {
                    bgcolor: "rgba(0,212,255,0.1)",
                  },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                }}
              >
                <Badge
                  badgeContent={getItemCount()}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#ff0080",
                      color: "white",
                      fontWeight: 600,
                      boxShadow: "0 0 10px rgba(255,0,128,0.8)",
                    },
                  }}
                >
                  <ShoppingCart />
                </Badge>
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              size={theme.breakpoints.down("sm") ? "small" : "medium"}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                borderRadius: 10,
                borderColor: "rgba(255,0,128,0.5)",
                color: "#ff0080",
                fontSize: { xs: "0.55rem", sm: "0.675rem" },
                px: { xs: 2, sm: 3 },
                minWidth: { xs: 80, sm: 100 },
                "&:hover": {
                  borderColor: "#ff0080",
                  bgcolor: "rgba(255,0,128,0.1)",
                },
              }}
            >
              {t("auth.signIn")}
            </Button>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size={theme.breakpoints.down("sm") ? "small" : "medium"}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                borderRadius: 10,
                background: "#ff0080",
                boxShadow: "0 0 0 rgba(0,212,255,0.1)",
                fontSize: { xs: "0.55rem", sm: "0.675rem" },
                px: { xs: 2, sm: 3 },
                minWidth: { xs: 80, sm: 100 },
                color: "#fff",
                "&:hover": {
                  background: "#cc0066",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {t("auth.signUp")}
            </Button>
          </>
        )}
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 1,
          ml: 2,
          direction: "ltr",
        }}
      >
        <ThemeModeToggle />
        <LanguageSwitcher />
      </Box>

      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="end"
        onClick={onDrawerToggle}
        sx={{
          display: { xs: "flex", md: "none" },
          ml: 0.5,
          direction: "ltr",
          "&:hover": {
            bgcolor: "rgba(0,212,255,0.1)",
          },
        }}
      >
        <MenuIcon />
      </IconButton>
    </Toolbar>
  );
};

export default HeaderTopBar;